import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { SystemUpdateService } from '@/services/system-update.service';
import type { Contractor } from "@/data/types";
import type { ContractorPayProfile } from "@/data/contractorPayProfile";
import { REALTIME_ROW_PATCHING_ENABLED, SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";
import type {
  OnboardingEntityType,
  OnboardingSessionStatus,
} from "@/data/contractor/onboarding/types";

function readMetadata(dbContractor: Record<string, unknown>): Record<string, unknown> {
  const metadata = dbContractor.metadata;
  return metadata && typeof metadata === "object" ? metadata as Record<string, unknown> : {};
}

function mapAvailabilityStatus(
  dbContractor: Record<string, unknown>,
  metadata: Record<string, unknown>,
): Contractor["availabilityStatus"] {
  const raw =
    metadata.availabilityStatus ??
    metadata.availability_status;

  if (raw === "Available" || raw === "Limited" || raw === "Unavailable") {
    return raw;
  }

  return dbContractor.status === "active" ? "Available" : "Unavailable";
}

function mapOnboardingStatus(status: unknown): Contractor["onboardingStatus"] {
  switch (status) {
    case "active":
      return "Approved";
    case "inactive":
      return "Inactive";
    case "suspended":
      return "Rejected";
    case "pending":
    default:
      return "Under Review";
  }
}

function readBool(
  dbContractor: Record<string, unknown>,
  metadata: Record<string, unknown>,
  dbKey: string,
  metadataKeys: string[],
): boolean {
  const dbValue = dbContractor[dbKey];
  if (typeof dbValue === "boolean") return dbValue;
  for (const key of metadataKeys) {
    const value = metadata[key];
    if (typeof value === "boolean") return value;
  }
  return false;
}

function readString(
  dbContractor: Record<string, unknown>,
  metadata: Record<string, unknown>,
  dbKeys: string[],
  metadataKeys: string[] = [],
): string {
  for (const key of dbKeys) {
    const value = dbContractor[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  for (const key of metadataKeys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

function readStringArray(
  dbContractor: Record<string, unknown>,
  metadata: Record<string, unknown>,
  dbKeys: string[],
  metadataKeys: string[] = [],
): string[] {
  for (const key of dbKeys) {
    const value = dbContractor[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    }
  }
  for (const key of metadataKeys) {
    const value = metadata[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    }
  }
  return [];
}

function mapDbContractorToContractor(dbContractor: Record<string, unknown>): Contractor {
  const metadata = readMetadata(dbContractor);
  const status = dbContractor.status;

  return {
    id: dbContractor.id as string,
    organizationId: (dbContractor.organization_id as string) || undefined,
    contractorType:
      dbContractor.contractor_type === "main_parent"
      || dbContractor.contractor_type === "sub_parent"
        ? dbContractor.contractor_type
        : null,
    companyName: (dbContractor.company_name as string) || (dbContractor.name as string) || "",
    contactName: (dbContractor.name as string) || (dbContractor.company_name as string) || "",
    phone: (dbContractor.phone as string) || "",
    email: (dbContractor.email as string) || "",
    serviceArea: readString(
      dbContractor,
      metadata,
      ["service_area"],
      ["serviceArea", "service_area"],
    ) || [
      dbContractor.city as string,
      dbContractor.state as string,
    ].filter(Boolean).join(", "),
    serviceRegions: readStringArray(
      dbContractor,
      metadata,
      ["service_regions"],
      ["serviceRegions", "service_regions"],
    ),
    tradeType: (dbContractor.trade as string) || "",
    active: status === "active",
    rating: Number(metadata.rating ?? 0),
    responsivenessRating: Number(dbContractor.responsiveness_rating ?? 0),
    notes: (dbContractor.notes as string) || "",
    insuranceOnFile: readBool(dbContractor, metadata, "insurance_on_file", ["insuranceOnFile", "insurance_on_file"]),
    insuranceExpiry: readString(
      dbContractor,
      metadata,
      ["insurance_expiry", "insurance_expiration_date"],
      ["insuranceExpiry", "insurance_expiry", "insuranceExpirationDate"],
    ),
    w9OnFile: readBool(dbContractor, metadata, "w9_on_file", ["w9OnFile", "w9_on_file"]),
    licenseNumber: readString(dbContractor, metadata, ["license_number"], ["licenseNumber", "license_number"]),
    complianceNotes: readString(dbContractor, metadata, ["compliance_notes"], ["complianceNotes", "compliance_notes"]),
    onboardingStatus: mapOnboardingStatus(status),
    agreementSigned: readBool(dbContractor, metadata, "agreement_signed", ["agreementSigned", "agreement_signed"]),
    tags: readStringArray(dbContractor, metadata, ["tags"], ["tags"]),
    branch: (metadata.branch as string) || "",
    joinedDate: (metadata.joinedDate as string) || (dbContractor.created_at as string) || "",
    availabilityStatus: mapAvailabilityStatus(dbContractor, metadata),
    maxWeeklyJobs: Number(metadata.maxWeeklyJobs ?? 0),
    serviceZipCodes: readStringArray(
      dbContractor,
      metadata,
      ["service_zip_codes"],
      ["serviceZipCodes", "service_zip_codes"],
    ),
    externalRef: metadata.externalRef as Contractor['externalRef'],
    marketplaceOptIn: readBool(dbContractor, metadata, "marketplace_opt_in", ["marketplaceOptIn", "marketplace_opt_in"]),
    marketplaceAdminOverride: (metadata.marketplaceAdminOverride as Contractor['marketplaceAdminOverride']) ?? undefined,
    availability: (metadata.availability as Contractor['availability']) ?? undefined,
    availabilityDeclaredAt: (metadata.availabilityDeclaredAt as string) ?? undefined,
    availabilityDeclaredBy: (metadata.availabilityDeclaredBy as string) ?? undefined,
    entityType:
      (dbContractor.entity_type as Contractor["entityType"])
      ?? (metadata.entityType as Contractor["entityType"])
      ?? undefined,
    parentEntityId:
      (dbContractor.parent_id as string)
      ?? (dbContractor.parent_entity_id as string)
      ?? (metadata.parentEntityId as string)
      ?? null,
    relationshipType:
      (dbContractor.relationship_type as Contractor["relationshipType"])
      ?? (metadata.relationshipType as Contractor["relationshipType"])
      ?? null,
    accountabilityMode:
      (dbContractor.accountability_mode as Contractor["accountabilityMode"])
      ?? (metadata.accountabilityMode as Contractor["accountabilityMode"])
      ?? undefined,
  };
}

function extractPayProfileFromContractor(dbContractor: Record<string, unknown>): ContractorPayProfile | null {
  const metadata = readMetadata(dbContractor);
  const pp = metadata.payProfile as Record<string, unknown> | undefined;
  if (!pp || !pp.id || !pp.contractorId) return null;
  return {
    id: pp.id as string,
    contractorId: pp.contractorId as string,
    allowedSpecTypes: (pp.allowedSpecTypes as ContractorPayProfile['allowedSpecTypes']) ?? [],
    totalPayoutAmount: Number(pp.totalPayoutAmount ?? 0),
    paymentStructure: (pp.paymentStructure as ContractorPayProfile['paymentStructure']) ?? "full_completion",
    paymentTrigger: (pp.paymentTrigger as ContractorPayProfile['paymentTrigger']) ?? "qa_pass",
    paymentSlaHours: Number(pp.paymentSlaHours ?? 48),
    fieldAddCapAmount: Number(pp.fieldAddCapAmount ?? 0),
    tierLevel: pp.tierLevel as ContractorPayProfile['tierLevel'] ?? undefined,
    isActive: Boolean(pp.isActive ?? true),
    createdAt: (pp.createdAt as string) ?? new Date().toISOString(),
    updatedAt: (pp.updatedAt as string) ?? new Date().toISOString(),
    notes: pp.notes as string | undefined,
  };
}

export const ContractorService = {
  /**
   * Create a contractor record from the intake onboarding flow.
   * Uses the correct DB column names (inferred from mapDbContractorToContractor).
   * Path-specific extras go into metadata JSONB.
   */
  async createFromIntake(data: {
    companyName: string;
    contactName: string;
    phone?: string;
    email: string;
    tradeType?: string;
    serviceArea?: string;
    branch?: string;
    licenseNumber?: string;
    tags?: string[];
    serviceZipCodes?: string[];
    notes?: string;
    /**
     * Optional onboarding projection controls.
     * If omitted, defaults to a submitted packet from intake.
     */
    onboarding?: {
      entityType?: OnboardingEntityType;
      parentEntityId?: string;
      status?: Extract<OnboardingSessionStatus, "draft" | "submitted" | "reupload_requested">;
      actorUserId?: string;
      correlationId?: string;
    };
  }): Promise<Contractor | null> {
    let createdContractorIdForCompensation: string | null = null;
    try {
      const organizationId = await getCurrentOrganizationId();
      const normalizedCompanyName = data.companyName.trim();
      const normalizedEmail = data.email.trim().toLowerCase();
      const correlationId =
        data.onboarding?.correlationId ??
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const actorUserId = data.onboarding?.actorUserId ?? "intake-system";

      // Idempotent contractor resolve: prefer org+email match, fallback to org+company_name.
      const { data: existingByEmail } = await supabase
        .from("contractors")
        .select("*")
        .eq("organization_id", organizationId)
        .ilike("email", normalizedEmail)
        .limit(1)
        .maybeSingle();

      const { data: existingByCompany } = existingByEmail
        ? { data: null as Record<string, unknown> | null }
        : await supabase
            .from("contractors")
            .select("*")
            .eq("organization_id", organizationId)
            .ilike("company_name", normalizedCompanyName)
            .limit(1)
            .maybeSingle();

      let dbContractor = (existingByEmail ?? existingByCompany) as Record<string, unknown> | null;
      let createdContractorId: string | null = null;

      const row = {
        organization_id: organizationId,
        company_name: normalizedCompanyName,
        name: data.contactName,
        phone: data.phone ?? null,
        email: normalizedEmail,
        trade: data.tradeType ?? null,
        status: "pending",
        notes: data.notes ?? null,
        license_number: data.licenseNumber ?? null,
        tags: data.tags ?? [],
        metadata: {
          branch: data.branch ?? "Tampa",
          joinedDate: new Date().toISOString().slice(0, 10),
          availabilityStatus: "Unavailable",
          maxWeeklyJobs: 0,
          serviceRegions: data.serviceArea ? [data.serviceArea] : [],
          serviceZipCodes: data.serviceZipCodes ?? [],
          rating: 0,
        },
      };

      if (!dbContractor) {
        const { data: created, error } = await supabase
          .from("contractors")
          .insert(row)
          .select()
          .single();
        if (error) {
          console.warn("Error creating contractor from intake:", error.message);
          return null;
        }
        dbContractor = (created as Record<string, unknown>) ?? null;
        createdContractorId = (created as { id?: string } | null)?.id ?? null;
        createdContractorIdForCompensation = createdContractorId;
      }

      if (!dbContractor?.id) return null;
      const contractorId = String(dbContractor.id);

      const entityType: OnboardingEntityType = data.onboarding?.entityType ?? "independent";
      const parentEntityId = data.onboarding?.parentEntityId ?? null;
      const sessionStatus =
        data.onboarding?.status ?? ("submitted" as Extract<OnboardingSessionStatus, "draft" | "submitted" | "reupload_requested">);
      const nowIso = new Date().toISOString();

      // Active session reuse policy.
      const { data: existingSession } = await supabase
        .from("onboarding_sessions")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("contractor_id", contractorId)
        .in("status", ["draft", "submitted", "reupload_requested"])
        .order("last_touched_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const isTokenUsable = (row: Record<string, unknown> | null | undefined): boolean => {
        if (!row) return false;
        const status = String(row.status ?? "");
        const expiresAt = String(row.expires_at ?? "");
        if (status !== "pending") return false;
        if (!expiresAt) return false;
        return new Date(expiresAt).getTime() > Date.now();
      };

      const createPendingToken = async (): Promise<Record<string, unknown>> => {
        const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
        const token = (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/-/g, "");
        const tokenId = typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const { data: createdToken, error: tokenError } = await supabase
          .from("onboarding_invite_tokens")
          .insert({
            id: tokenId,
            organization_id: organizationId,
            contractor_id: contractorId,
            entity_type: entityType,
            created_by: actorUserId,
            created_at: nowIso,
            expires_at: expiresAt,
            status: "pending",
            token,
            parent_id: parentEntityId,
          })
          .select("*")
          .single();
        if (tokenError || !createdToken) {
          throw tokenError ?? new Error("Failed to create onboarding invite token");
        }
        return createdToken as Record<string, unknown>;
      };

      let tokenRow: Record<string, unknown> | null = null;
      if (existingSession?.token_id) {
        const { data: linkedToken } = await supabase
          .from("onboarding_invite_tokens")
          .select("*")
          .eq("id", existingSession.token_id as string)
          .maybeSingle();
        if (isTokenUsable(linkedToken as Record<string, unknown> | null)) {
          tokenRow = linkedToken as Record<string, unknown>;
        }
      }
      if (!tokenRow) {
        const { data: pendingToken } = await supabase
          .from("onboarding_invite_tokens")
          .select("*")
          .eq("organization_id", organizationId)
          .eq("contractor_id", contractorId)
          .eq("status", "pending")
          .gt("expires_at", nowIso)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (isTokenUsable(pendingToken as Record<string, unknown> | null)) {
          tokenRow = pendingToken as Record<string, unknown>;
        }
      }
      if (!tokenRow) {
        tokenRow = await createPendingToken();
      }

      const sessionDraft = {
        contactName: data.contactName,
        contactEmail: normalizedEmail,
        contactPhone: data.phone ?? "",
        entityType,
        parentEntityId: parentEntityId ?? undefined,
        serviceRegions: data.serviceArea ? [data.serviceArea] : [],
        serviceZipCodes: data.serviceZipCodes ?? [],
        tradeType: data.tradeType ?? "",
        name: data.companyName,
      };

      let sessionId: string | null = null;
      if (existingSession) {
        sessionId = String(existingSession.id);
        const { error: sessionPatchError } = await supabase
          .from("onboarding_sessions")
          .update({
            token_id: tokenRow.id as string,
            entity_type: entityType,
            parent_entity_id: parentEntityId,
            status: sessionStatus,
            draft: sessionDraft,
            last_touched_at: nowIso,
          })
          .eq("id", sessionId);
        if (sessionPatchError) {
          throw sessionPatchError;
        }
      } else {
        sessionId =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const { error: sessionCreateError } = await supabase
          .from("onboarding_sessions")
          .insert({
            id: sessionId,
            organization_id: organizationId,
            contractor_id: contractorId,
            token_id: tokenRow.id as string,
            entity_type: entityType,
            parent_entity_id: parentEntityId,
            current_step: "identity",
            completed_steps: [],
            status: sessionStatus,
            draft: sessionDraft,
            started_at: nowIso,
            last_touched_at: nowIso,
            submitted_at: sessionStatus === "submitted" ? nowIso : null,
          });
        if (sessionCreateError) {
          throw sessionCreateError;
        }
      }

      // Best-effort audit trail.
      const auditBase = {
        organization_id: organizationId,
        actor: actorUserId,
        at: nowIso,
        metadata: {
          contractor_id: contractorId,
          organization_id: organizationId,
          correlation_id: correlationId,
          source: "contractor_intake",
        },
      };
      const auditRows: Record<string, unknown>[] = [];
      if (createdContractorId) {
        auditRows.push({
          ...auditBase,
          id:
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          action: "draft_updated",
          reason: "contractor_created_from_intake",
          session_id: sessionId,
          token_id: tokenRow.id as string,
        });
      }
      auditRows.push({
        ...auditBase,
        id:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        action: "invite_created",
        reason: existingSession ? "onboarding_session_reused" : "onboarding_invite_created",
        session_id: sessionId,
        token_id: tokenRow.id as string,
      });
      auditRows.push({
        ...auditBase,
        id:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        action: existingSession ? "draft_updated" : "step_completed",
        reason: existingSession ? "onboarding_session_reused" : "onboarding_session_created",
        session_id: sessionId,
        token_id: tokenRow.id as string,
      });
      await supabase.from("onboarding_audit_events").insert(auditRows);

      const contractorName = (dbContractor as any)?.company_name ?? 'Contractor'; // eslint-disable-line @typescript-eslint/no-explicit-any
      void SystemUpdateService.post({
        domain: 'onboarding',
        event:  'contractor_activated',
        body:   `System: Contractor Activated — ${contractorName}`,
      });
      return mapDbContractorToContractor(dbContractor);
    } catch (err) {
      // Compensation: if we created the contractor in this call and onboarding artifacts failed,
      // remove the contractor to avoid orphan "contractor-without-session".
      if (createdContractorIdForCompensation) {
        await supabase
          .from("contractors")
          .delete()
          .eq("id", createdContractorIdForCompensation);
      }
      console.warn("Exception creating contractor from intake:", err);
      return null;
    }
  },

  async fetchContractors(): Promise<Contractor[]> {
    try {
      const { data, error } = await supabase
        .from("contractors")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.warn("Error fetching contractors from Supabase:", error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbContractorToContractor);
    } catch (err) {
      console.warn("Exception fetching contractors:", err);
      return [];
    }
  },

  async fetchContractorById(id: string): Promise<Contractor | null> {
    try {
      const { data, error } = await supabase
        .from("contractors")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.warn(`Error fetching contractor ${id}:`, error.message);
        return null;
      }

      return data ? mapDbContractorToContractor(data) : null;
    } catch (err) {
      console.warn(`Exception fetching contractor ${id}:`, err);
      return null;
    }
  },

  async fetchActiveContractors(): Promise<Contractor[]> {
    try {
      const { data, error } = await supabase
        .from("contractors")
        .select("*")
        .eq("status", "active")
        .order("company_name", { ascending: true });

      if (error) {
        console.warn("Error fetching active contractors:", error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbContractorToContractor);
    } catch (err) {
      console.warn("Exception fetching active contractors:", err);
      return [];
    }
  },

  async createContractor(contractor: Partial<Contractor>): Promise<Contractor | null> {
    try {
      const metadata = {
        serviceArea: contractor.serviceArea,
        serviceRegions: contractor.serviceRegions,
        availabilityStatus: contractor.availabilityStatus,
        maxWeeklyJobs: contractor.maxWeeklyJobs,
        serviceZipCodes: contractor.serviceZipCodes,
        externalRef: contractor.externalRef,
      };
      const dbContractor: Record<string, unknown> = {
        id: contractor.id,
        company_name: contractor.companyName,
        contact_name: contractor.contactName,
        phone: contractor.phone,
        email: contractor.email,
        trade_type: contractor.tradeType,
        active: contractor.active,
        rating: contractor.rating,
        responsiveness_rating: contractor.responsivenessRating,
        notes: contractor.notes,
        insurance_on_file: contractor.insuranceOnFile,
        insurance_expiry: contractor.insuranceExpiry,
        w9_on_file: contractor.w9OnFile,
        license_number: contractor.licenseNumber,
        compliance_notes: contractor.complianceNotes,
        onboarding_status: contractor.onboardingStatus,
        agreement_signed: contractor.agreementSigned,
        tags: contractor.tags,
        branch: contractor.branch,
        joined_date: contractor.joinedDate,
        metadata,
      };

      const { data, error } = await supabase
        .from("contractors")
        .insert(dbContractor)
        .select()
        .single();

      if (error) {
        console.warn("Error creating contractor:", error.message);
        return null;
      }

      return data ? mapDbContractorToContractor(data) : null;
    } catch (err) {
      console.warn("Exception creating contractor:", err);
      return null;
    }
  },

  async updateContractor(id: string, updates: Partial<Contractor>): Promise<Contractor | null> {
    try {
      const dbContractor: Record<string, unknown> = {};
      const metadataPatch: Record<string, unknown> = {};

      if (updates.companyName !== undefined) dbContractor.company_name = updates.companyName;
      if (updates.contactName !== undefined) dbContractor.contact_name = updates.contactName;
      if (updates.phone !== undefined) dbContractor.phone = updates.phone;
      if (updates.email !== undefined) dbContractor.email = updates.email;
      if (updates.serviceArea !== undefined) metadataPatch.serviceArea = updates.serviceArea;
      if (updates.serviceRegions !== undefined) metadataPatch.serviceRegions = updates.serviceRegions;
      if (updates.tradeType !== undefined) dbContractor.trade = updates.tradeType;
      if (updates.active !== undefined) dbContractor.status = updates.active ? "active" : "inactive";
      if (updates.rating !== undefined) dbContractor.rating = updates.rating;
      if (updates.responsivenessRating !== undefined) dbContractor.responsiveness_rating = updates.responsivenessRating;
      if (updates.notes !== undefined) dbContractor.notes = updates.notes;
      if (updates.insuranceOnFile !== undefined) dbContractor.insurance_on_file = updates.insuranceOnFile;
      if (updates.insuranceExpiry !== undefined) dbContractor.insurance_expiry = updates.insuranceExpiry;
      if (updates.w9OnFile !== undefined) dbContractor.w9_on_file = updates.w9OnFile;
      if (updates.licenseNumber !== undefined) dbContractor.license_number = updates.licenseNumber;
      if (updates.complianceNotes !== undefined) dbContractor.compliance_notes = updates.complianceNotes;
      if (updates.onboardingStatus !== undefined) dbContractor.onboarding_status = updates.onboardingStatus;
      if (updates.agreementSigned !== undefined) dbContractor.agreement_signed = updates.agreementSigned;
      if (updates.tags !== undefined) dbContractor.tags = updates.tags;
      if (updates.branch !== undefined) dbContractor.branch = updates.branch;
      if (updates.joinedDate !== undefined) dbContractor.joined_date = updates.joinedDate;
      if (updates.availabilityStatus !== undefined) metadataPatch.availabilityStatus = updates.availabilityStatus;
      if (updates.maxWeeklyJobs !== undefined) metadataPatch.maxWeeklyJobs = updates.maxWeeklyJobs;
      if (updates.serviceZipCodes !== undefined) metadataPatch.serviceZipCodes = updates.serviceZipCodes;
      if (updates.marketplaceOptIn !== undefined) metadataPatch.marketplaceOptIn = updates.marketplaceOptIn;
      if (updates.externalRef !== undefined) metadataPatch.externalRef = updates.externalRef;

      if (Object.keys(metadataPatch).length > 0) {
        const { data: current, error: metadataLoadError } = await supabase
          .from("contractors")
          .select("metadata")
          .eq("id", id)
          .maybeSingle();
        if (metadataLoadError) {
          console.warn(`Failed to load contractor metadata for ${id}:`, metadataLoadError.message);
          return null;
        }
        const currentMetadata =
          current?.metadata && typeof current.metadata === "object"
            ? (current.metadata as Record<string, unknown>)
            : {};
        dbContractor.metadata = { ...currentMetadata, ...metadataPatch };
      }

      const { data, error } = await supabase
        .from("contractors")
        .update(dbContractor)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.warn(`Error updating contractor ${id}:`, error.message);
        return null;
      }

      return data ? mapDbContractorToContractor(data) : null;
    } catch (err) {
      console.warn(`Exception updating contractor ${id}:`, err);
      return null;
    }
  },

  subscribeToContractors(callback: (contractors: Contractor[]) => void): () => void {
    try {
      let disposed = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let inFlight = false;
      let snapshot: Contractor[] | null = null;

      const refresh = async () => {
        if (disposed || inFlight) return;
        inFlight = true;
        try {
          recordRealtimeMetric({ source: "contractor-service", event: "full-refetch", table: "contractors" });
          const contractors = await ContractorService.fetchContractors();
          if (!disposed) {
            snapshot = contractors;
            callback(contractors);
          }
        } finally {
          inFlight = false;
        }
      };

      const scheduleRefresh = () => {
        if (disposed) return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          void refresh();
        }, 2_000);
      };

      const applyRealtimePatch = (payload: { eventType?: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
        if (!REALTIME_ROW_PATCHING_ENABLED || !snapshot) {
          scheduleRefresh();
          return;
        }

        const eventType = String(payload.eventType ?? "").toUpperCase();
        const entityId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
        if (!entityId) {
          scheduleRefresh();
          return;
        }

        if (eventType === "DELETE") {
          snapshot = snapshot.filter((contractor) => contractor.id !== entityId);
        } else if (payload.new) {
          const next = mapDbContractorToContractor(payload.new);
          const exists = snapshot.some((contractor) => contractor.id === next.id);
          snapshot = exists
            ? snapshot.map((contractor) => contractor.id === next.id ? next : contractor)
            : [next, ...snapshot];
        } else {
          scheduleRefresh();
          return;
        }

        recordRealtimeMetric({
          source: "contractor-service",
          event: "patch",
          table: "contractors",
          entityId,
          detail: SUPABASE_SYNC_DEBUG ? { eventType } : undefined,
        });
        if (!disposed) callback(snapshot);
      };

      const channel = supabase
        .channel('contractors-changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'contractors' },
          (payload) => applyRealtimePatch(payload as any)
        )
        .subscribe();

      return () => {
        disposed = true;
        if (timer) clearTimeout(timer);
        supabase.removeChannel(channel);
      };
    } catch (err) {
      console.warn("Exception subscribing to contractors:", err);
      return () => {};
    }
  },

  async updateMarketplaceAdminOverride(
    contractorId: string,
    override: Contractor["marketplaceAdminOverride"],
  ): Promise<{ error: string | null }> {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from("contractors")
        .select("metadata, name, company_name")
        .eq("id", contractorId)
        .single();
      if (fetchErr) return { error: fetchErr.message };

      const prev = (existing?.metadata as Record<string, unknown>) ?? {};
      const next = { ...prev, marketplaceAdminOverride: override ?? null };

      const { error: updateErr } = await supabase
        .from("contractors")
        .update({ metadata: next })
        .eq("id", contractorId);

      if (!updateErr) {
        const isEligible = override ?? false;
        const contractorLabel = String(
          (existing as Record<string, unknown>)?.company_name ||
          (existing as Record<string, unknown>)?.name ||
          contractorId
        );
        void SystemUpdateService.post({
          domain: 'offers',
          event:  isEligible ? 'eligibility_override_applied' : 'eligibility_removed',
          body:   `System: Installer Eligibility ${isEligible ? 'Override Applied' : 'Removed'} — ${contractorLabel}`,
        });
      }

      return { error: updateErr?.message ?? null };
    } catch (err) {
      return { error: String(err) };
    }
  },

  /** Extract pay profiles from contractor metadata rows. */
  async fetchPayProfiles(): Promise<ContractorPayProfile[]> {
    try {
      const { data, error } = await supabase
        .from("contractors")
        .select("id, metadata")
        .order("created_at", { ascending: false });

      if (error || !data) return [];
      return data
        .map((row) => extractPayProfileFromContractor(row as Record<string, unknown>))
        .filter((pp): pp is ContractorPayProfile => pp !== null);
    } catch (err) {
      console.warn("Exception fetching pay profiles:", err);
      return [];
    }
  },

  async savePayProfile(
    contractorId: string,
    profile: ContractorPayProfile,
  ): Promise<{ error: string | null }> {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from("contractors")
        .select("metadata")
        .eq("id", contractorId)
        .single();
      if (fetchErr) return { error: fetchErr.message };

      const prev = (existing?.metadata as Record<string, unknown>) ?? {};
      const next = {
        ...prev,
        payProfile: {
          id: profile.id,
          contractorId: profile.contractorId,
          allowedSpecTypes: profile.allowedSpecTypes,
          totalPayoutAmount: profile.totalPayoutAmount,
          paymentStructure: profile.paymentStructure,
          paymentTrigger: profile.paymentTrigger,
          paymentSlaHours: profile.paymentSlaHours,
          fieldAddCapAmount: profile.fieldAddCapAmount,
          tierLevel: profile.tierLevel ?? null,
          isActive: profile.isActive,
          createdAt: profile.createdAt,
          updatedAt: profile.updatedAt,
          notes: profile.notes ?? null,
        },
      };

      const { error: updateErr } = await supabase
        .from("contractors")
        .update({ metadata: next })
        .eq("id", contractorId);

      return { error: updateErr?.message ?? null };
    } catch (err) {
      return { error: String(err) };
    }
  },

  async updateAvailability(
    contractorId: string,
    availability: Contractor['availability'],
    actor: { id: string; role: string },
  ): Promise<{ error: string | null }> {
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from("contractors")
        .select("metadata")
        .eq("id", contractorId)
        .single();
      if (fetchErr) return { error: fetchErr.message };

      const prev = (existing?.metadata as Record<string, unknown>) ?? {};
      const next = {
        ...prev,
        availability,
        availabilityDeclaredAt: new Date().toISOString(),
        availabilityDeclaredBy: `${actor.role}:${actor.id}`,
      };

      const { error: updateErr } = await supabase
        .from("contractors")
        .update({ metadata: next })
        .eq("id", contractorId);

      return { error: updateErr?.message ?? null };
    } catch (err) {
      return { error: String(err) };
    }
  },
};
